import type { Record } from "@suse-o11y/record";
import React, { type FC } from "react";
import { useSelector } from "react-redux";

import { selectRightPaneCanGoBack } from "../../../../app/state/right-pane/RightPaneSelectors";
import { EmptyStateWidget, type MessagePaneProps } from "../../../../components/_empty-state-widget";

import { EastPaneBackButton } from "./EastPaneBackButton";
import { EastPaneLayout } from "./EastPaneLayout";

export type NotFoundType =
	| "Component"
	| "Event"
	| "Span"
	| "SpanEvent"
	| "Group"
	| "Relation"
	| "Events"
	| "PinnedItems";

export interface EastPaneNotFoundProps {
	type: NotFoundType;
}

export const EastPaneNotFound: FC<EastPaneNotFoundProps> = ({ type }) => {
	const canGoBackInChain = useSelector(selectRightPaneCanGoBack);

	return (
		<EastPaneLayout
			center={true}
			data-testid="east-pane-error-not-found"
			header={canGoBackInChain && <EastPaneBackButton />}
		>
			<EmptyStateWidget {...NOT_FOUND_CONTENT[type]} layout="compact" />
		</EastPaneLayout>
	);
};

const NOT_FOUND_CONTENT: Record<NotFoundType, Omit<MessagePaneProps, "layout">> = {
	Component: {
		title: "Component not found",
		message:
			"The component you are trying to view does not exist at this moment in time or you don't have the permission to view it.",
	},
	Event: {
		title: "Event not found",
		message: "The event you are trying to view does not exist or you don't have the permission to view it.",
	},
	Span: {
		title: "Span not found",
		message: "The span you are trying to view does not exist or you don't have the permission to view it.",
	},
	SpanEvent: {
		title: "Span event not found",
		message: "The span event you are trying to view does not exist or you don't have the permission to view it.",
	},
	Group: {
		title: "Group not found",
		message:
			"The group you are trying to view does not exist at this moment in time or you don't have the permission to view it.",
	},
	Relation: {
		title: "Relation not found",
		message:
			"The relation you are trying to view does not exist at this moment in time or you don't have the permission to view it.",
	},
	Events: {
		title: "Events not found",
		message: "The events you are trying to view do not exist.",
	},
	PinnedItems: {
		title: "Your pinned items list is empty",
		message:
			"Pin items you find useful during your investigation to quickly switch between them: monitor statuses, related resources, configurations, changes, events and more.",
		// learnMore: "", TODO STAC-19309: add documentation link https://stackstate.atlassian.net/browse/STAC-19309
	},
};
